import { Button, Menu, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconCheck, IconChevronDown, IconLock } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { BILLING_DISPLAY_TIMEZONE } from '@/pages/Billing/utils'
import { useOrpc } from '@/hooks/useClient'

dayjs.extend(utc)
dayjs.extend(timezone)

// The hand-pickable routes (billingV2SetRoute's input), labelled as the Routing column
// labels them. 'none' is deliberately absent: it is the sweep's "not decided yet", never
// a biller's choice.
const ROUTE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'stedi', label: 'Stedi' },
]

function routeLabel(route: string): string {
  return ROUTE_OPTIONS.find((option) => option.value === route)?.label ?? route
}

type RoutingControlProps = {
  encounterId: string
  submissionRoute: string
  /** The Routing column's label for the current route ("Bridge", "Not set"). */
  label: string
  /** Submitted to an external route — the chip reads "Managed by <label>" with a lock. */
  managed?: boolean
  claimStatus: string
  claimSubmittedAt: string | null
  externalClaimId: string | null
  /** Σ counting insurance payments on the case — any money locks the route (the backend rule). */
  insurancePaidCents: number
}

const LOCKED_HINT = 'Route is locked once an insurance payment is recorded'
const MANUAL_ONLY_HINT =
  'An insurance payment is recorded but nothing was sent through this route — only Manual is available'

/**
 * The Routing cell as a control: a dropdown chip (outline, chevron) that opens a menu over
 * Manual, Bridge and Stedi; a pick applies at once through billingV2SetRoute (which
 * journals route_changed). A case that already counts as submitted — claim_submitted_at, a
 * mid-chain external claim id, or a hand-marked Submitted status — is re-routed only after a
 * red confirm modal, and the request then carries `discardSubmission: true`; the backend
 * refuses it without the flag. Once an insurance payment is recorded the chip is inert
 * (lock icon, tooltip only) — the backend refuses the change with the same text — except
 * when nothing ever reached the clearinghouse (no external id, no submission stamp): the
 * money was recorded by hand, and the case may still be moved to Manual as a correction,
 * with no confirm, its status untouched.
 */
export const RoutingControl = ({
  encounterId,
  submissionRoute,
  label,
  managed = false,
  claimStatus,
  claimSubmittedAt,
  externalClaimId,
  insurancePaidCents,
}: RoutingControlProps) => {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const setRoute = useMutation(orpc.billingV2SetRoute.mutationOptions())

  const locked = insurancePaidCents > 0
  const manualOnly = locked && !claimSubmittedAt && !externalClaimId && submissionRoute !== 'manual'
  const hasSubmission =
    Boolean(claimSubmittedAt) || Boolean(externalClaimId) || claimStatus === 'billed'
  const currentLabel = routeLabel(submissionRoute)
  const isExternal = submissionRoute === 'bridge' || submissionRoute === 'stedi'
  const chipText = managed ? `Managed by ${label}` : label

  const submit = async (route: string, discardSubmission: boolean) => {
    try {
      await setRoute.mutateAsync({
        encounterId,
        submissionRoute: route as 'manual' | 'bridge' | 'stedi',
        ...(discardSubmission ? { discardSubmission: true } : {}),
      })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      showNotification({ color: 'green', message: `Routing set to ${routeLabel(route)}` })
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to change the route',
      })
    }
  }

  const describeSubmission = (): string => {
    if (claimSubmittedAt) {
      const date = dayjs(claimSubmittedAt).tz(BILLING_DISPLAY_TIMEZONE).format('MM/DD/YYYY')
      return `This claim was submitted to ${currentLabel} on ${date}.`
    }
    if (externalClaimId) return `A submission to ${currentLabel} is in progress.`
    return 'This claim is marked Submitted.'
  }

  const pick = (route: string) => {
    if (route === submissionRoute) return
    if (!hasSubmission || manualOnly) {
      void submit(route, false)
      return
    }

    const nextLabel = routeLabel(route)
    modals.openConfirmModal({
      title: 'Discard the current submission?',
      children: (
        <Text size="sm">
          {describeSubmission()} Changing the route to {nextLabel} discards that submission here and
          the claim goes back to Ready to Bill
          {isExternal ? ` — nothing is cancelled at ${currentLabel}` : ''}.
        </Text>
      ),
      labels: { confirm: 'Discard and change route', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void submit(route, true),
    })
  }

  if (locked && !manualOnly) {
    return (
      <Tooltip label={LOCKED_HINT} withArrow multiline w={220}>
        <span className="inline-flex min-h-7 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-sm font-medium text-slate-600">
          <IconLock size={14} className="shrink-0" />
          {chipText}
        </span>
      </Tooltip>
    )
  }

  return (
    <Menu position="bottom-start" shadow="md" width={manualOnly ? 260 : 160}>
      <Menu.Target>
        <Button
          variant="default"
          size="compact-sm"
          radius="sm"
          title={manualOnly ? MANUAL_ONLY_HINT : 'Change route'}
          loading={setRoute.isPending}
          leftSection={managed ? <IconLock size={14} /> : undefined}
          rightSection={<IconChevronDown size={14} />}
          // The cell is narrow: grow and wrap instead of a one-line ellipsis.
          styles={{
            root: { height: 'auto', minHeight: 28, paddingBlock: 4, fontWeight: 500 },
            label: { whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.3 },
          }}
        >
          {chipText}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{manualOnly ? MANUAL_ONLY_HINT : 'Routing'}</Menu.Label>
        {(manualOnly
          ? ROUTE_OPTIONS.filter((option) => option.value === 'manual')
          : ROUTE_OPTIONS
        ).map((option) => {
          const current = option.value === submissionRoute
          return (
            <Menu.Item
              key={option.value}
              disabled={current}
              leftSection={
                current ? <IconCheck size={14} /> : <span className="inline-block w-3.5" />
              }
              onClick={() => pick(option.value)}
            >
              {option.label}
            </Menu.Item>
          )
        })}
      </Menu.Dropdown>
    </Menu>
  )
}
